import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Avatar, AvatarType } from '@/lib/types/common/Avatar';
import { getAvatarsByType } from '@/lib/config/avatars';
import PanelFFXIV from '@/app/components/PanelFFXIV';
import TextFFXIV from '@/app/components/TextFFXIV';
import { useTranslation } from '@/hooks/useTranslation';
import { AUDIO, useAudio } from '@/app/providers/AudioProvider';

interface AvatarSelectorProps {
    isOpen: boolean;
    currentAvatarId?: string;
    onSelect: (avatar: Avatar) => void;
    onClose: () => void;
}

const roleLabels: Record<AvatarType, string> = {
    tank: 'Tank',
    healer: 'Healer',
    dps: 'DPS',
    crafter: 'Crafter',
    gatherer: 'Gatherer',
    other: 'Other',
};

const AvatarSelector: React.FC<AvatarSelectorProps> = ({
    isOpen,
    currentAvatarId,
    onSelect,
    onClose
}) => {
    const { t } = useTranslation();
    const avatarsByType = getAvatarsByType();
    const [showOtherCategory, setShowOtherCategory] = useState(false);
    const { play } = useAudio();

    if (!isOpen) return null;

    const handleAvatarClick = (avatar: Avatar) => {
        onSelect(avatar);
        onClose();
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[100000] flex items-center justify-center"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                className="relative max-w-4xl max-h-[90vh] overflow-auto"
            >
                <PanelFFXIV className="!p-4 md:!py-3 !md:px-4 border-2 border-x-[#c0a270] border-y-[#e0c290]">
                    <div className="flex justify-between items-center mb-6">
                        <TextFFXIV text={t('selectYourAvatar')} className="text-2xl" />
                        <div
                            className="absolute top-1 right-1 cursor-pointer z-10 hover:brightness-125"
                            onMouseEnter={() => { play(AUDIO.HOVER) }}
                            onClick={() => { play(AUDIO.CLICK); onClose(); }}
                        >
                            <Image
                                alt="Close Avatar Selector"
                                height={25}
                                width={25}
                                src="/map/close.png"
                            />
                        </div>
                    </div>

                    <div className="space-y-4 md:space-y-7">
                        {(Object.entries(avatarsByType) as [AvatarType, Avatar[]][])
                            .filter(([roleType, avatars]) => {
                                if (roleType === 'other') {
                                    return showOtherCategory && avatars.length > 0;
                                }
                                return avatars.length > 0;
                            })
                            .map(([roleType, avatars]) => (
                                <div key={roleType} className="">
                                    <div className="flex gap-2 items-center">
                                        <Image
                                            src={`/avatar/categories/${roleType}.png`}
                                            alt={avatars[0].name}
                                            width={20}
                                            height={20}
                                        />
                                        <TextFFXIV text={roleLabels[roleType]} className="text-lg font-bold" />
                                    </div>
                                    <div className="grid grid-cols-6 md:grid-cols-10 gap-1 md:gap-2">
                                        {avatars.map((avatar) => (
                                            <motion.button
                                                key={avatar.id}
                                                onMouseEnter={() => { play(AUDIO.HOVER) }}
                                                onClick={() => { play(AUDIO.CLICK); handleAvatarClick(avatar) }}
                                                className={`flex items-center justify-center rounded-lg  relative w-[44px] h-[44px] group ${currentAvatarId === avatar.id
                                                    ? "border-[2px] border-[#FFF6CA] shadow-[0px_0px_10px_#FFF6CA]" : " hover:border-[2px] hover:border-[#FFF6CA] hover:shadow-[0px_0px_10px_#FFF6CA]"}`}
                                            >
                                                <Image
                                                    src={`/avatar/${avatar.filename}`}
                                                    alt={avatar.name}
                                                    width={40}
                                                    height={40}
                                                />
                                                {/* <TooltipFFXIV>
                                                    <span>{avatar.name}</span>
                                                </TooltipFFXIV> */}
                                            </motion.button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                    </div>

                    {/* Hidden button to reveal "other" category */}
                    {!showOtherCategory && (
                        <button
                            onClick={() => setShowOtherCategory(true)}
                            className="absolute bottom-4 right-4 w-4 h-4 opacity-[10%] bg-black"
                            title="Reveal hidden avatars"
                        ></button>
                    )}
                </PanelFFXIV>
            </motion.div>
        </div >,
        document.body
    );
};

export default AvatarSelector;